import React from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Link from 'components/shared/link';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import classNames from 'classnames/bind';

import Heading from 'components/shared/heading';

import VideoIntercom from './images/video-intercom.svg';
import FaceRecognition from './images/face-recognition.svg';
import MobileAccess from './images/mobile-access.svg';
import CloudBased from './images/cloud-based.svg';
import HealthCheck from './images/health-check.svg';
import KeyCards from './images/key-cards.svg';

import styles from './products.module.scss';

const cx = classNames.bind(styles);

const MOTION_EASE = [0.25, 0.1, 0.25, 1];

const wrapperVariants = {
    appear: {
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.4,
        },
    },
};
const itemsVariants = {
    initial: {
        opacity: 0,
    },
    appear: {
        opacity: 1,
        transition: {
            duration: 0.7,
            ease: MOTION_EASE,
        },
    },
};

const Products = ({ className: additionalClassName = null, title = null, background = 'dark' }) => {
    const [sectionRef, inView] = useInView({ threshold: 0.4, triggerOnce: true });

    const {
        wpSharedBlock: {
            acf: { title: titleShared, items },
        },
        videoIntercom,
        faceRecognition,
        mobileAccess,
        keyCards,
        cloudBased,
        healthCheck,
    } = useStaticQuery(graphql`
    query {
      wpSharedBlock(slug: { eq: "products" }) {
        acf {
          title
          items {
            title
            url
            productName
          }
        }
      }
      videoIntercom: file(relativePath: { eq: "shared/products/video-intercom.png" }) {
        childImageSharp {
          gatsbyImageData(width: 178)
        }
      }
      faceRecognition: file(relativePath: { eq: "shared/products/face-recognition.png" }) {
        childImageSharp {
          gatsbyImageData(width: 205)
        }
      }
      mobileAccess: file(relativePath: { eq: "shared/products/mobile-access.png" }) {
        childImageSharp {
          gatsbyImageData(width: 205)
        }
      }
      keyCards: file(relativePath: { eq: "shared/products/key-cards.png" }) {
        childImageSharp {
          gatsbyImageData(width: 196)
        }
      }
      cloudBased: file(relativePath: { eq: "shared/products/cloud-based.png" }) {
        childImageSharp {
          gatsbyImageData(width: 220)
        }
      }
      healthCheck: file(relativePath: { eq: "shared/products/health-check.png" }) {
        childImageSharp {
          gatsbyImageData(width: 205)
        }
      }
    }
  `);

    const itemImages = {
        videoIntercom: {
            initial: VideoIntercom,
            appear: videoIntercom,
        },
        faceRecognition: {
            initial: FaceRecognition,
            appear: faceRecognition,
        },
        mobileAccess: {
            initial: MobileAccess,
            appear: mobileAccess,
        },
        keyCards: {
            initial: KeyCards,
            appear: keyCards,
        },
        cloudBased: {
            initial: CloudBased,
            appear: cloudBased,
        },
        healthCheck: {
            initial: HealthCheck,
            appear: healthCheck,
        },
    };

    const className = cx('wrapper', `wrapper_background_${background}`, additionalClassName);

    const headingColor = {
        dark: 'tertiary',
        white: 'primary',
        gray: 'primary',
    };

    return (
        <section className={className} ref={sectionRef}>
            <div className={cx('container', 'inner')}>
                <Heading className={cx('title')} color={headingColor[background]} tag="h2" size="xl">
                    {title || titleShared}
                </Heading>

                <motion.div
                    className={cx('items-wrapper')}
                    animate={inView ? 'appear' : 'initial'}
                    variants={wrapperVariants}
                >
                    <div className={cx('items-inner')}>
                        {items.map(({ title, url, productName }, index) => {
                            const initialImage = itemImages[productName].initial;
                            const appearImage = itemImages[productName].appear;
                            return (
                                <Link className={cx('item', productName)} to={url} key={index}>
                                    <div className={cx('item-image-wrapper')}>
                                        <img className={cx('item-initial-image')} src={initialImage} alt="" />
                                        <motion.div variants={itemsVariants}>
                                            <GatsbyImage
                                                className={cx('item-appear-image')}
                                                image={getImage(appearImage)}
                                                alt=""
                                            />
                                        </motion.div>
                                    </div>
                                    <Heading className={cx('item-title')} tag="h3" size="lg" innerHTML={title} />
                                </Link>
                            );
                        })}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

Products.propTypes = {
    className: PropTypes.string,
    title: PropTypes.string,
    background: PropTypes.oneOf(['dark', 'white', 'gray']),
};


export default Products;
